import { cn } from "@/lib/utils";
import type { FileRecord } from "@cohesive/api-client";
import { FileText } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  computeHtmlSrcDocFingerprint,
  prepareSrcDocHtml,
} from "../../../lib/htmlIframeSrcDoc";
import { getPresignedUrlExpiresAtMs } from "../../../lib/presignedUrl";
import { getFileNameFromPath } from "../FileRecordCard/FileRecordCard";

// Match the exact same zoom constants as ArtifactCardCanvas.
const REFERENCE_WIDTH = 350;
const BASE_SCALE = 0.35;
const EFFECTIVE_VIEWPORT_WIDTH = REFERENCE_WIDTH / BASE_SCALE; // ≈1000px

export type HtmlFileRecordCardCanvasProps = Pick<
  FileRecord,
  "url" | "path" | "updated_at"
> & {
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  /** True while the parent NodeResizer is active — disables iframe pointer
   *  events so the resize cursor isn't captured by the iframe. */
  resizing?: boolean;
  /** True when the node is in interactive mode and the iframe should receive
   *  pointer events. Controlled by EntityNode's gesture disambiguator. */
  isInteractive?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
};

/**
 * Full-bleed HTML preview for HTML file record canvas nodes.
 *
 * Fetches the HTML body and sets it as iframe `srcDoc` after injecting
 * force-light declarations. The iframe remounts only when the content
 * fingerprint changes, not on presigned URL rotation.
 *
 * Scale mirrors ArtifactCardCanvas: ResizeObserver → contentScale →
 * `transform: scale()` anchored at origin-top-left.
 */
export function HtmlFileRecordCardCanvas({
  url,
  path,
  updated_at,
  className,
  style,
  selected,
  resizing = false,
  isInteractive = false,
  onClick,
  onDoubleClick,
}: HtmlFileRecordCardCanvasProps) {
  const iframeActive = isInteractive && !resizing;

  const cardRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [cardWidth, setCardWidth] = useState<number>(() => {
    return typeof style?.width === "number" && style.width > 0
      ? style.width
      : REFERENCE_WIDTH;
  });

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const w = entry.contentRect.width;
        if (w > 0) setCardWidth(w);
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const contentScale = cardWidth / EFFECTIVE_VIEWPORT_WIDTH;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const srcDocFingerprintRef = useRef<string | null>(null);
  const [srcDoc, setSrcDoc] = useState<string | null>(null);
  const [srcDocFingerprint, setSrcDocFingerprint] = useState<string | null>(
    null,
  );
  const [isVisible, setIsVisible] = useState(false);

  const handleIframeLoad = useCallback(() => {
    setIsVisible(true);
  }, []);

  const s3Path = useMemo(() => {
    if (!url) return null;
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }, [url]);

  const urlRef = useRef(url);
  useEffect(() => {
    urlRef.current = url;
  }, [url]);

  const urlExpiresAtMs = useMemo(() => getPresignedUrlExpiresAtMs(url), [url]);

  useEffect(() => {
    if (!s3Path || !urlRef.current) return;
    let cancelled = false;

    void fetch(urlRef.current)
      .then((res) => (res.ok ? res.text() : undefined))
      .then((text) => {
        if (cancelled || text == null) return;

        const fingerprint = computeHtmlSrcDocFingerprint(text, undefined);
        if (fingerprint === srcDocFingerprintRef.current) return;

        srcDocFingerprintRef.current = fingerprint;
        setSrcDoc(prepareSrcDocHtml(text, { editable: false }));
        setSrcDocFingerprint(fingerprint);
        setIsVisible(false);
      })
      .catch(() => {
        // Keep existing content on error — don't go blank.
      });

    return () => {
      cancelled = true;
    };
  }, [s3Path, updated_at, urlExpiresAtMs]);

  const displayTitle = (path && getFileNameFromPath(path)) || "HTML";

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative overflow-hidden rounded-xl",
        "aspect-square w-full",
        !resizing && "transition-shadow duration-200",
        "hover:shadow-2xl",
        selected && "ring-2 ring-canvas-selection/60",
        onClick && "cursor-pointer",
        className,
      )}
      style={{ containerType: "inline-size", ...style }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <div
        className="relative h-full w-full overflow-hidden bg-white"
        style={{ colorScheme: "light" }}
      >
        {!isVisible && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
            <FileText className="h-8 w-8 text-muted-foreground" />
          </div>
        )}

        <div className="relative overflow-hidden w-full h-full">
          <div
            className={cn(
              "absolute inset-0 z-0 origin-top-left transition-opacity duration-200 ease-out",
              isVisible ? "opacity-100" : "opacity-0",
            )}
            style={{
              transform: `scale(${contentScale})`,
              width: `${100 / contentScale}%`,
              height: `${100 / contentScale}%`,
            }}
          >
            {srcDoc && srcDocFingerprint && (
              <iframe
                key={srcDocFingerprint}
                ref={iframeRef}
                srcDoc={srcDoc}
                className="w-full h-full border-0"
                style={{
                  pointerEvents: iframeActive ? "auto" : "none",
                  colorScheme: "light",
                }}
                sandbox="allow-scripts"
                title={displayTitle}
                onLoad={handleIframeLoad}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
